import React, { useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

/**
 * Selector en cascada bloque → subred → IP libre.
 *
 * Se usa tanto para la IP virtual de un grupo (CARP/VRRP/HSRP) como para dar
 * una IP física a un miembro que todavía no tiene ninguna en IPAM: si el
 * desplegable de asignaciones existentes está vacío, no hay nada que elegir y
 * hay que poder tomar una IP libre de una subred.
 */
function FreeIpPicker({ companyId, value, onChange, defaultSubnetId = '', compact = true }) {
  const [blocks, setBlocks] = useState([]);
  const [blockId, setBlockId] = useState('');
  const [subnets, setSubnets] = useState([]);
  const [subnetId, setSubnetId] = useState(defaultSubnetId ? String(defaultSubnetId) : '');
  const [freeIps, setFreeIps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.getIpamBlocks(companyId).then(r => setBlocks(r.data || [])).catch(() => {});
  }, [companyId]);

  useEffect(() => {
    if (!blockId || !companyId) { setSubnets([]); return; }
    api.getIpamBlockSubnets(Number(blockId), companyId)
      .then(r => setSubnets(r.data?.subnets || []))
      .catch(() => {});
  }, [blockId, companyId]);

  useEffect(() => {
    if (!subnetId || !companyId) { setFreeIps([]); return; }
    setLoading(true);
    api.getIpamSubnetMap(Number(subnetId), companyId, 256)
      .then(r => {
        const free = (r.data?.generated_ips || []).filter(e => e.status === 'free' && !e.is_gateway);
        setFreeIps(free);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subnetId, companyId]);

  const cls = compact ? 'form-select form-select-sm' : 'form-select';
  // Ojo: `value` puede venir del formulario con una IP que ya estaba puesta
  // antes de abrir el selector (p. ej. la VIP actual). Solo cuenta como
  // seleccionada si está entre las IPs libres que se han cargado.
  const seleccionada = freeIps.some(e => e.ip === value) ? value : '';

  return (
    <div className="d-flex flex-column gap-2">
      <select className={cls} value={blockId}
        onChange={e => { setBlockId(e.target.value); setSubnetId(''); onChange(''); }}>
        <option value="">Selecciona bloque…</option>
        {blocks.map(b => <option key={b.id} value={b.id}>{b.cidr}</option>)}
      </select>
      <select className={cls} value={subnetId} disabled={!blockId}
        onChange={e => { setSubnetId(e.target.value); onChange(''); }}>
        <option value="">Selecciona subred…</option>
        {subnets.map(s => (
          <option key={s.id} value={s.id}>
            {s.cidr}{s.site ? ` · ${s.site}` : ''}{s.type ? ` [${s.type.toUpperCase()}]` : ''}
          </option>
        ))}
      </select>
      <select className={`${cls} font-monospace`} value={seleccionada}
        onChange={e => onChange(e.target.value)}
        disabled={!subnetId || loading}>
        <option value="">
          {loading ? 'Cargando IPs…' : !subnetId ? 'Selecciona subred primero'
            : freeIps.length === 0 ? 'Sin IPs libres en esta subred' : 'Selecciona IP libre…'}
        </option>
        {freeIps.map(e => <option key={e.ip} value={e.ip}>{e.ip}</option>)}
      </select>
      {seleccionada && (
        <div className="d-flex align-items-center gap-2 text-success small">
          <CheckIcon style={{ width: 14, height: 14 }} />
          IP seleccionada: <strong className="font-monospace">{seleccionada}</strong>
        </div>
      )}
    </div>
  );
}

export default FreeIpPicker;
